import { observer } from 'mobx-react';
import { Tooltip } from 'antd';
import type { ComparedResult } from '@/api/validationRuns';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';

// ─────────────────────────────────────────────────────────────────────────────
// Every repeat, every outcome (SI-07, mockup 09 note 4): one dot per trial, never collapsed to the best run. Each dot
// carries its outcome in words — on hover, on focus and to a screen reader ("Trial 2: Fail") — and opens that trial.
// A trial that did not complete is a HOLLOW dot: counted and shown, never dropped or read as a pass. Colour is never the
// only signal: the side's summary ("1 of 3 passed") is always written beside the dots.
// ─────────────────────────────────────────────────────────────────────────────

export const TrialDots = observer(function TrialDots({
  side,
  onOpen,
}: {
  side: ComparedResult | null | undefined;
  onOpen: (resultId: number) => void;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  if (!side) return null;
  const trials = side.trials?.length ? side.trials : [{ repeat: 1, resultId: side.resultId, outcome: side.completed ? (side.passed ? 'Pass' : 'Fail') : 'DidNotComplete', words: 'Trial 1' } as const];

  return (
    <span role="list" aria-label="Trials" style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
      {trials.map((t) => {
        const fill = t.outcome === 'Pass' ? colors.green[600] : t.outcome === 'Fail' ? colors.red[600] : 'transparent';
        const ring = t.outcome === 'DidNotComplete' ? (isDark ? colors.slate[400] : colors.slate[500]) : fill;
        return (
          <span role="listitem" key={t.resultId}>
            <Tooltip title={`${t.words} · opens the trial`}>
              <button
                type="button"
                aria-label={t.words}
                onClick={() => onOpen(t.resultId)}
                style={{
                  width: 14,
                  height: 14,
                  padding: 0,
                  borderRadius: '50%',
                  border: `2px solid ${ring}`,
                  background: fill,
                  cursor: 'pointer',
                  display: 'inline-block',
                }}
              />
            </Tooltip>
          </span>
        );
      })}
    </span>
  );
});
